import { APP_NAME } from "../constants";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";

export interface AddLocationInstructionSectionProps {
    stepNumber?: number;
    freeForeverAvailable?: boolean;
}

const AddLocationInstructionSection = (props: AddLocationInstructionSectionProps) => {
  const { stepNumber, freeForeverAvailable } = props;

  const mainTitle = () => {
    switch(stepNumber) {
      case 1:
        return "Business Information";
      case 2:
        return "Business Hours";
      case 3:
        return "Door Lock Settings";
      case 4:
        return "Options & Amenities";
      case 5:
        return freeForeverAvailable ? "Free Forever Hosting" : "Select Hosting Plan";
      case 6:
        return "Payment Information";
      default:
        return "Getting Started";
    }
  };
  const mainDescription = () => {
    switch(stepNumber) {
      case 1:
        return "Enter the location business address and contact details. The address will be verified via Google Address Verify API to ensure users will be able to find the location.";
      case 2:
        return "Set the location business hours. These are the normal operating business hours for the location.";
      case 3:
        return "Set the location restroom door lock settings. These settings specifiy restroom access. If you do not have a lock on your restroom door, simply toggle 'Require Door Lock Code for Restroom Entry' off. ";
      case 4:
        return "Select all amentities offered at this location. Select all that apply. Users are able to filter on these amentities to find exactly what they are looking for so don't miss anything."
      case 5:
        return freeForeverAvailable ? "This location qualifies for Free Forever Hosting! No catch! Thank you for being one our first hosts on the platform!" : "Select the hosting plan that fits your location. Each plan is a recurring monthly subscription and contains no hidden fees or charges."
      case 6:
        return "Enter recurring subscription payment information for this location."
      default:
        return "Follow the steps on the right to complete your setup.";
    }
  };
  const stepContent = () => {
    switch(stepNumber) {
      case 1:
        return (
        <><MDBox mb={1.5}>
            <MDTypography fontWeight="medium" variant="h6">
              1. Enter Business Name
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
              Enter the location business name as on the building.
            </MDTypography>
          </MDBox>

          <MDBox mb={1.5}>
            <MDTypography fontWeight="medium" variant="h6">
              2. Enter Address
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
              Enter the location street address and city. Then select the state and enter the zip code. 
            </MDTypography>
          </MDBox>
          <MDBox mb={1.5}>
            <MDTypography fontWeight="medium" variant="h6">
              3. Enter Phone Number
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
              Enter the business phone number for the location. This should be a live number so users may contact the location.
            </MDTypography>
          </MDBox>
          <MDBox mb={1.5}>
            <MDTypography fontWeight="medium" variant="h6">
              4. Click Next to Continue
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
              After entering the location details, click the Next button to validate the address and proceed to the next step.
            </MDTypography>
          </MDBox>
          </>
          );
          case 2:
          return (
          <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Select 'Open 24 Hours' option
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                If the location is open 24 hours a day, ensure this is selected. If not, click the toggle to select specific business hours.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Select Specific Business Hours
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When open 24 hours is turned off, you will see options for each day of the week. For each day, select the business hours for the location. If the location is closed for that day, simply click the 'Closed' toggle for that day.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                3. Click Next to Continue
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After selecting the location business hours, click the Next button to proceed to the next step.
              </MDTypography>
            </MDBox>
            </>
            );
          case 3:
          return (
          <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Select 'Require Door Lock Code for Restroom Entry' option
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                If the location restroom requires a door lock code for entry, ensure this is selected. If not, click the toggle to turn off door lock requirements.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Enter Door Lock Code
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When a door lock is required, you will see an option for Door Lock Code. Enter a door lock code that unlocks the restroom door at this location. This should be a numerical code the user will enter into the keypad on the door lock to gain access to the restroom.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                3. Enter Time per Visit
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When a door lock is required, you will see an option for Time per Visit (minutes). This is time allowed per visit. The user will get additional time if requested. Enter how many minutes a normal visit should be at this location.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                4. Select 'Door Lock Uses QR Reader' option
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When a door lock is required, you will see an option for 'Door Lock Uses QR Reader'. If your door lock has a QR Reader on it, you can enable this for users. The app will present the user the QR code to use for entry.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                5. Select 'Always Require Door Code' option
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When a door lock is required, you will see an option for 'Always Require Door Code'. This means the location's restroom is always locked and is accessible with a door lock code. Toggle this option 'off' to lock the restroom during specific hours.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                6. Select Specific Door Lock Hours
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                When 'Always Require Door Code' is turned off, you will see options for each day of the week. For each day, select the hours the restroom will be locked for the location. If the location is locked all day for that day, simply click the 'Locked All Day' toggle for that day. If the location is not locked that day, simply click the 'Not Locked' toggle for that day.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                7. Click Next to Continue
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After selecting the location door lock settings, click the Next button to proceed to the next step.
              </MDTypography>
            </MDBox>
            </>
            );
          case 4:
          return (
          <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Select Options and Amenities
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                Click each amenity button to apply this to the location. If the button is outlined, the amenity is NOT selected. If the button is a solid color, the amenity is selected. To un-select an amentity, simply click it again.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Click Next to Continue
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After selecting the location options and amenities, click the Next button to proceed to the next step.
              </MDTypography>
            </MDBox>
            </>
            );
          case 5:
          return (
            freeForeverAvailable &&
          <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Enter your Representative
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                If you found {APP_NAME} with the help of one of our representatives, please enter their code or name.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Click Save Location
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After entering a representative, click the Save Location button to save this location.
              </MDTypography>
            </MDBox>
            </>
            || <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Select Hosting Plan
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                Select the hosting plan for this location. The most common is the 'Host all Users' plan, as this draws the most customers and is the best value. Premium Hosting means the location only shows on the map for users who have purchased the Premium {APP_NAME} Membership.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Click Next to Continue
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After selecting the location hosting plan, click the Next button to proceed to the next step.
              </MDTypography>
            </MDBox>
            </>
            );
          case 6:
          return (
          <><MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                1. Enter your Payment Information
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                Credit card information is NOT collected, nor stored by {APP_NAME}. All transactions and stored information are handled by Stripe&trade; Charges will appear as 'Macke's Software LLC'
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                2. Enter your Representative
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary"sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                If you found {APP_NAME} with the help of one of our representatives, please enter their code or name.
              </MDTypography>
            </MDBox>
            <MDBox mb={1.5}>
              <MDTypography fontWeight="medium" variant="h6">
                3. Click Process Payment
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                After entering payment information, click the Process Payment button to save this location.
              </MDTypography>
            </MDBox>
            </>);
          default:
            return null;
    }
  };


  return <MDBox>
          <MDTypography
            variant="h5"
            fontWeight="medium"
          >
            {mainTitle()}
          </MDTypography>

          <MDTypography
            variant="body2"
            mb={2}sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}
          >
            {mainDescription()}
          </MDTypography>

          {stepContent()}
          {stepNumber == 6 && 
          <MDBox mb={1.5}>
              <MDTypography variant="caption" color="text.primary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                * Subscriptions are charged on a monthly basis starting on 'today' and recurring on this day of each month. Subscriptions can be cancelled any time. Cancellation or failure of payment will remove this location from the map on the {APP_NAME} app.
              </MDTypography>
            </MDBox>
          ||
          <MDBox mb={1.5}>
              <MDTypography variant="caption" color="text.primary" sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}>
                * All settings and selected options can be changed on the settings page for the location.
              </MDTypography>
            </MDBox>
          }
        </MDBox>
};

export default AddLocationInstructionSection;